import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, Upload, ArrowRight } from "lucide-react";
import { GALLERY_CATEGORIES } from "@/data/site";

// Gallery renders clearly-labeled placeholder cards until real project photos
// are supplied. No stock imagery is used — this preserves trust.
export default function Gallery() {
  const [active, setActive] = useState(GALLERY_CATEGORIES[0]);

  return (
    <section id="gallery" data-testid="gallery-section" className="rs-section bg-white">
      <div className="rs-container">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-2xl">
            <p className="rs-overline mb-4">Project Gallery</p>
            <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
              Real work.{" "}
              <span className="text-[#F97316]">Coming soon.</span>
            </h2>
            <p className="mt-5 text-base md:text-lg text-slate-600 leading-relaxed">
              We&apos;re curating a before &amp; after gallery of our own completed projects — no stock
              imagery. Check back soon, or ask us for recent photos directly.
            </p>
          </div>
          <a href="#contact" className="rs-btn-secondary self-start md:self-end">
            Ask to see recent work
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>

        <div className="mt-10 flex flex-wrap gap-2.5" data-testid="gallery-tabs">
          {GALLERY_CATEGORIES.map((cat) => (
            <button
              key={cat}
              data-testid={`gallery-tab-${cat.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setActive(cat)}
              className={`px-4 py-2.5 rounded-full text-sm font-medium transition-all ${
                active === cat
                  ? "bg-[#0A192F] text-white shadow-md"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.35 }}
            className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
          >
            {[0, 1, 2].map((idx) => (
              <PlaceholderCard key={`${active}-${idx}`} label={active} idx={idx} />
            ))}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}

function PlaceholderCard({ label, idx }) {
  return (
    <div
      data-testid={`gallery-placeholder-${idx}`}
      className="group relative rounded-2xl overflow-hidden bg-gradient-to-br from-[#F1F5F9] to-[#E2E8F0] aspect-[4/3] flex flex-col items-center justify-center text-center p-8 border border-slate-200"
    >
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "repeating-linear-gradient(45deg, #0A192F 0 1px, transparent 1px 14px)",
        }}
      />
      <div className="relative h-14 w-14 rounded-2xl bg-white border border-slate-200 grid place-items-center shadow-sm">
        <Camera className="h-6 w-6 text-[#F97316]" />
      </div>
      <div className="relative mt-5 text-xs uppercase tracking-[0.22em] font-bold text-[#F97316]">
        {label}
      </div>
      <div className="relative mt-2 font-display font-semibold text-lg text-[#0A192F] leading-tight">
        Project photo coming soon
      </div>
      <div className="relative mt-1 text-xs text-slate-500">
        Real before &amp; after images from RS Handyman Services
      </div>
      <div className="relative mt-5 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500">
        <Upload className="h-3.5 w-3.5" />
        Upload slot {idx + 1}
      </div>
    </div>
  );
}
